import { useEffect, useState } from 'react';

const WINDOW_VISIBILITY_EVENT = getWindowVisibilityChangeName();

/**
 * Returns true if the window is visible, false otherwise.
 */
export function useWindowVisibility(): boolean {
  const [windowVisible, setWindowVisible] = useState(!isWindowHidden());

  useEffect(() => {
    if (WINDOW_VISIBILITY_EVENT) {
      const listener = () => {
        const windowVisible = !isWindowHidden();
        setWindowVisible(windowVisible);
      };

      window.addEventListener(WINDOW_VISIBILITY_EVENT, listener, false);
      return () => {
        window.removeEventListener(WINDOW_VISIBILITY_EVENT, listener);
      };
    }
  }, []);

  return windowVisible;
}

/**
 * Returns true if the window has been hidden for the specified number of milliseconds
 * or longer.
 */
export function useWindowHidden(ms?: number): boolean {
  const [hidden, setHidden] = useState(!isWindowHidden());

  const visible = useWindowVisibility();

  useEffect(() => {
    if (visible) {
      // If the window is visible, mark the window as not hidden immediately.
      setHidden(false);
      return void 0;
    } else if (typeof ms === 'number' && ms > -1) {
      // If the window is not visible, then start a timeout to mark it hidden
      const timeout = setTimeout(() => setHidden(true), ms);
      return () => clearTimeout(timeout);
    }
  }, [ms, visible]);

  return hidden;
}

function getWindowVisibilityChangeName() {
  if (typeof document === 'undefined') {
    // Running in unit tests...
    return undefined;
  }
  if (typeof document.hidden !== 'undefined') {
    return 'visibilitychange';
  } else if (typeof (document as any).msHidden !== 'undefined') {
    return 'msvisibilitychange';
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    return 'webkitvisibilitychange';
  }

  return undefined;
}

export function isWindowHidden() {
  return !!document.hidden || !!(document as any).msHidden || !!(document as any).webkitHidden;
}
